import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import BarChartIcon from "@mui/icons-material/BarChart";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Card, CardContent, CardHeader, Grid, Stack, Typography, useMediaQuery, useTheme, } from "@mui/material";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDeleteIntraSquadMatch } from "../api/IntraSquadMatchApiProvider";
import { useRetrieveUserIntraSquadMatchPermissions } from "../api/PermissionApiProvider";
import { useRetrieveIntraSquadMatch, useRetrievePlayersByTeamId, useRetrieveTeam, useRetrieveTeamPreferences, } from "../ApiProvider";
import { ErrorView } from "../nav/Error";
import { Loading } from "../nav/Loading";
import { NotFound } from "../nav/NotFound";
import { findPlayerById } from "../players/utils";
import { formatScorer, formatSentOff } from "../stats/ScorerFormatter";
import { findPlayerIntraSquadMatchStatByPlayerId } from "../stats/utils";
import { SquadCard } from "../teams/SquadCard";
import { DeleteDialog } from "../utils/DeleteDialog";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { ResponsiveActionMenu, } from "../utils/ResponsiveActionMenu";
import { useNumericParams } from "../utils/useNumericParams";
import { MatchDateTimeHeader } from "./MatchDateTimeHeader";
import { MatchScoreboard } from "./MatchScoreboard";
const IntraSquadMatch = () => {
    const { id } = useNumericParams();
    const navigate = useNavigate();
    const [showDelete, setShowDelete] = React.useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { data: userIntraSquadMatchPermissions, isLoading: userIntraSquadMatchPermissionsIsLoading, error: userIntraSquadMatchPermissionsError, } = useRetrieveUserIntraSquadMatchPermissions({ intraSquadMatchId: id });
    const { canModify = false, canView = false, canModifyStats = false, } = userIntraSquadMatchPermissions || {};
    const { isLoading: intraSquadMatchIsLoading, error: intraSquadMatchError, data: intraSquadMatch, } = useRetrieveIntraSquadMatch({ id, enabled: canView });
    const { isLoading: useRetrieveTeamIsLoading, error: useRetrieveTeamError, data: team, } = useRetrieveTeam({ id: intraSquadMatch?.team });
    const mutation = useDeleteIntraSquadMatch({
        intraSquadMatch,
        team,
        onMutate: () => { },
        onSuccess: () => {
            return navigate(`/teams/${intraSquadMatch?.team}`);
        },
        onError: () => { },
    });
    const { isLoading: useRetrieveTeamPreferenceIsLoading, error: useRetrieveTeamPreferenceError, data: teamPreference, } = useRetrieveTeamPreferences({ teamId: intraSquadMatch?.team });
    const { data: players, error: playersError, isLoading: playersIsLoading, } = useRetrievePlayersByTeamId({ teamId: intraSquadMatch?.team });
    const getPlayer = findPlayerById(players || []);
    const getPlayerIntraSquadMatchStat = findPlayerIntraSquadMatchStatByPlayerId(intraSquadMatch?.playerIntraSquadMatchStats || []);
    // The lineup is the authoritative hydrated squad. Prefer the fuller roster
    // record (squad number, etc.) where available, else fall back to the summary
    // so a player who has since left the team still renders.
    const teamAPlayers = intraSquadMatch?.teamAPlayers?.map((summary) => getPlayer(summary.id) ?? summary);
    const teamBPlayers = intraSquadMatch?.teamBPlayers?.map((summary) => getPlayer(summary.id) ?? summary);
    const teamAScorers = teamAPlayers
        ?.map((player) => formatScorer(player, getPlayerIntraSquadMatchStat(player.id)))
        .filter((scorer) => scorer !== undefined);
    const teamBScorers = teamBPlayers
        ?.map((player) => formatScorer(player, getPlayerIntraSquadMatchStat(player.id)))
        .filter((scorer) => scorer !== undefined);
    const teamASentOff = teamAPlayers
        ?.map((player) => formatSentOff(player, getPlayerIntraSquadMatchStat(player.id)))
        .filter((name) => name !== undefined);
    const teamBSentOff = teamBPlayers
        ?.map((player) => formatSentOff(player, getPlayerIntraSquadMatchStat(player.id)))
        .filter((name) => name !== undefined);
    if (userIntraSquadMatchPermissionsIsLoading) {
        return _jsx(Loading, {});
    }
    if (userIntraSquadMatchPermissionsError) {
        return _jsx(ErrorView, { error: userIntraSquadMatchPermissionsError });
    }
    if (!canView) {
        return _jsx(NotFound, {});
    }
    if (intraSquadMatchIsLoading ||
        mutation.isLoading ||
        playersIsLoading ||
        useRetrieveTeamPreferenceIsLoading ||
        useRetrieveTeamIsLoading) {
        return _jsx(Loading, {});
    }
    if (id === undefined) {
        return _jsx(NotFound, {});
    }
    if (useRetrieveTeamError ||
        intraSquadMatchError ||
        useRetrieveTeamPreferenceError ||
        playersError ||
        !intraSquadMatch ||
        !teamPreference ||
        !team) {
        return _jsx(ErrorView, {});
    }
    const handleCloseDeleteDialog = () => {
        setShowDelete(false);
    };
    const handleDelete = () => {
        mutation.mutate();
    };
    const actions = [
        ...(canModify || canModifyStats
            ? [
                {
                    icon: _jsx(BarChartIcon, {}),
                    label: "Edit Stats",
                    to: `/teams/${team.id}/intra-squad-matches/${id}/stats/update/`,
                },
            ]
            : []),
        ...(canModify
            ? [
                {
                    icon: _jsx(EditIcon, {}),
                    label: "Edit Match",
                    to: `/teams/${team.id}/intra-squad-matches/${id}/update`,
                },
                {
                    icon: _jsx(DeleteIcon, {}),
                    label: "Delete",
                    onClick: () => setShowDelete(true),
                },
            ]
            : []),
    ];
    return (_jsxs(React.Fragment, { children: [_jsx(ErrorSnackbar, { open: mutation.isError, message: "Failed to delete match. Please try again!" }), _jsx(DeleteDialog, { open: showDelete, title: "Delete Intra-Squad Match?", onCancel: handleCloseDeleteDialog, onConfirm: handleDelete }), _jsxs(Card, { sx: { mb: 3 }, children: [_jsx(CardHeader, { title: _jsxs(Stack, { direction: "row", alignItems: "center", justifyContent: "space-between", children: [_jsx(Box, { sx: { flex: 1 } }), _jsx(MatchDateTimeHeader, { kickOff: intraSquadMatch.kickOff, subtitle: _jsx(Link, { style: { textDecoration: "none" }, to: `/teams/${team.id}`, children: _jsx(Typography, { variant: "body2", color: "primary.main", sx: { "&:hover": { textDecoration: "underline" } }, children: team.name }) }) }), _jsx(Box, { sx: { flex: 1, display: "flex", justifyContent: "flex-end" }, children: (canModify || canModifyStats) && (_jsx(ResponsiveActionMenu, { actions: actions, isMobile: isMobile })) })] }) }), _jsx(CardContent, { children: _jsx(MatchScoreboard, { team1: { name: teamPreference.teamAName }, team2: { name: teamPreference.teamBName }, score1: intraSquadMatch.intraSquadMatchResult?.teamAScore, score2: intraSquadMatch.intraSquadMatchResult?.teamBScore, scorers1: teamAScorers, scorers2: teamBScorers, sentOff1: teamASentOff, sentOff2: teamBSentOff }) })] }), _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, xs: 12, md: 6, children: _jsx(SquadCard, { title: _jsx(Typography, { align: "center", variant: "subtitle2", children: teamPreference.teamAName }), players: teamAPlayers || [], getPlayerStat: getPlayerIntraSquadMatchStat }) }), _jsx(Grid, { item: true, xs: 12, md: 6, children: _jsx(SquadCard, { title: _jsx(Typography, { align: "center", variant: "subtitle2", children: teamPreference.teamBName }), players: teamBPlayers || [], getPlayerStat: getPlayerIntraSquadMatchStat }) })] }), intraSquadMatch.intraSquadMatchResult?.matchReport?.trim() && (_jsxs(Card, { sx: { mt: 3 }, children: [_jsx(CardHeader, { title: _jsx(Typography, { variant: "subtitle2", textAlign: "center", children: "Match Report" }) }), _jsx(CardContent, { children: _jsx(Typography, { variant: "body1", whiteSpace: "pre-line", children: intraSquadMatch.intraSquadMatchResult.matchReport }) })] }))] }));
};
export default IntraSquadMatch;
